import React from 'react';
import { RoleInfoCell, RoleStatusBadge, RoleActionButtons } from '@molecules';

export interface Role {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  permissions: Array<{
    id: string;
    name: string;
    displayName: string;
    resource: string;
    action: string;
  }>;
}

export interface RoleTableProps {
  roles: Role[];
  searchTerm: string;
  onManagePermissions?: (role: Role) => void;
  onEditRole?: (role: Role) => void;
  onDeleteRole?: (role: Role) => void;
  isDeleteDisabled?: (role: Role) => boolean;
  showActions?: boolean;
}

export const RoleTable: React.FC<RoleTableProps> = ({
  roles,
  searchTerm,
  onManagePermissions,
  onEditRole,
  onDeleteRole,
  isDeleteDisabled,
  showActions = true
}) => {
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="bg-background-primary rounded-lg border border-border-primary overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              {showActions && (
                <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {filteredRoles.length === 0 ? (
              <tr>
                <td colSpan={showActions ? 5 : 4} className="px-6 py-8 text-center text-text-secondary">
                  {searchTerm ? 'No roles found matching your search.' : 'No roles found.'}
                </td>
              </tr>
            ) : (
              filteredRoles.map((role) => (
                <tr key={role.id} className="hover:bg-background-secondary">
                  <td className="px-6 py-4">
                    <RoleInfoCell
                      displayName={role.displayName}
                      name={role.name}
                      color={role.color}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-text-secondary">
                      {role.description || '—'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-text-primary">
                      {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <RoleStatusBadge isActive={role.isActive} />
                  </td>
                  {showActions && (
                    <td className="px-6 py-4 text-right">
                      <RoleActionButtons
                        onManagePermissions={onManagePermissions ? () => onManagePermissions(role) : undefined}
                        onEdit={onEditRole ? () => onEditRole(role) : undefined}
                        onDelete={onDeleteRole ? () => onDeleteRole(role) : undefined}
                        isDeleteDisabled={isDeleteDisabled?.(role) || false}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
